// /web/src/pages/ProjectDetail.jsx
import React from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "../lib/api";
import "../styles/project-detail.css";

export default function ProjectDetail() {
  const { id } = useParams();
  const api = useApi();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [project, setProject] = React.useState(null);

  const [inputs, setInputs] = React.useState([]);
  const [hasInputs, setHasInputs] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const p = await api(`/projects/${id}`);
        if (!mounted) return;
        setProject(p);

        try {
          const rows = await api(`/projects/${id}/inputs`);
          if (!mounted) return;
          const ok = Array.isArray(rows) && rows.length > 0;
          setHasInputs(ok);
          setInputs(ok ? rows.slice().sort((a, b) => a.floor_index - b.floor_index) : []);
        } catch {
          if (!mounted) return;
          setHasInputs(false);
          setInputs([]);
        }

        setError("");
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load project");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id, api]);

  const buildingUse = project?.building_use ? toTitleCase(project.building_use) : "—";
  const localAuthority = project?.local_authority ? toTitleCase(project.local_authority) : "—";
  const occupancyRatio =
    project?.occupancy_ratio !== undefined && project?.occupancy_ratio !== null
      ? project.occupancy_ratio
      : "—";
  const floorsCount = project?.floors_count ?? "—";
  const projectName = project?.name || "Project";

  const labels = computeLabels(inputs);
  const areaSummary = inputs.map((row, idx) => ({
    floor: labels[idx] || `Floor ${row.floor_index}`,
    gea: fmt(row.gea_m2),
    gia: fmt(row.gia_m2),
    nia: fmt(row.nia_m2),
    f2f: fmt(row.floor_to_floor_height_m),
  }));

  const calculators = getCalculators(id);

  if (loading) {
    return (
      <div className="pd-main pd-project-detail-page">
        <section className="pd-section">
          <div className="pd-container pd-project-detail">
            <p className="pd-muted">Loading project…</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pd-main pd-project-detail-page">
      <section className="pd-section">
        <div className="pd-container pd-project-detail">
          <div className="pd-project-header">
            <div>
              <h1 className="pd-h2">{projectName}</h1>
              <p className="pd-muted">
                {buildingUse} · {project?.postcode || "—"} · {localAuthority}
              </p>
            </div>

            <div className="pd-project-header__actions">
              <Link className="pd-btn pd-btn--ghost" to="/dashboard">
                Back to dashboard
              </Link>
              <Link className="pd-btn pd-btn--ghost" to={`/projects/${id}/massing`}>
                3D Massing
              </Link>
              <Link className="pd-btn pd-btn--primary" to={`/projects/${id}/edit`}>
                Edit project
              </Link>
            </div>
          </div>

          {error && <p className="pd-muted">Error: {error}</p>}

          <div className="pd-project-grid">
            <div className="pd-project-left">
              <section className="pd-block">
                <h3 className="pd-h3">Project summary</h3>
                <dl className="pd-summary-list">
                  <div>
                    <dt>Building use</dt>
                    <dd>{buildingUse}</dd>
                  </div>
                  <div>
                    <dt>Postcode &amp; authority</dt>
                    <dd>
                      {project?.postcode || "—"} · {localAuthority}
                    </dd>
                  </div>
                  <div>
                    <dt>Occupancy ratio</dt>
                    <dd>{occupancyRatio}</dd>
                  </div>
                  <div>
                    <dt>Floors</dt>
                    <dd>{floorsCount}</dd>
                  </div>
                </dl>
              </section>

              <section className="pd-block">
                <div className="pd-area-head">
                  <h3 className="pd-h3">Area summary</h3>
                </div>

                {hasInputs ? (
                  <div className="pd-area-table">
                    <div className="pd-area-row pd-area-row--head">
                      <span>Floor</span>
                      <span>GEA (m²)</span>
                      <span>GIA (m²)</span>
                      <span>NIA (m²)</span>
                      <span>Floor to floor (m)</span>
                    </div>
                    {areaSummary.map(row => (
                      <div className="pd-area-row" key={row.floor}>
                        <span>{row.floor}</span>
                        <span>{row.gea}</span>
                        <span>{row.gia}</span>
                        <span>{row.nia}</span>
                        <span>{row.f2f}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="pd-muted">Add areas and heights to unlock calculators.</p>
                )}

                <Link className="pd-btn pd-btn--ghost pd-edit-areas" to={`/projects/${id}/areas`}>
                  {hasInputs ? "Edit areas" : "Input areas"}
                </Link>
              </section>
            </div>

            <div className="pd-project-right">
              <section className="pd-block">
                <div className="pd-calcs-head">
                  <h3 className="pd-h3">Calculators</h3>
                  <p className="pd-muted">
                    Run tools against this project’s data. Tokens are charged per run.
                  </p>
                </div>

                <div className="pd-calcs-grid">
                  {calculators.map(calc => (
                    <article
                      key={calc.key}
                      className={`pd-calc-card pd-calc-card--${calc.colorClass} ${
                        calc.status === "coming-soon" ? "is-disabled" : ""
                      }`}
                    >
                      <div className="pd-calc-card__top">
                        <h4 className="pd-h4">{calc.name}</h4>
                        {typeof calc.tokens === "number" && (
                          <span className="pd-token-pill">
                            {calc.tokens} token{calc.tokens !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>

                      {calc.desc && <p className="pd-muted pd-calc-desc">{calc.desc}</p>}

                      <div className="pd-calc-card__footer">
                        {calc.status === "coming-soon" ? (
                          <span className="pd-coming-soon">Coming soon</span>
                        ) : (
                          <Link className="pd-btn pd-btn--primary small" to={calc.to}>
                            Open
                          </Link>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function getCalculators(projectId) {
  return [
    {
      key: "lift",
      name: "Lift Traffic Analysis",
      desc: "Simulate lift performance, waiting times and handling capacity.",
      tokens: 5,
      status: "active",
      colorClass: "lift",
      to: `/projects/${projectId}/calculators/lifts`,
    },
    {
      key: "wc",
      name: "WC Provision",
      desc: "Check sanitary provision against guidance and local policy.",
      tokens: 1,
      status: "active",
      colorClass: "wc",
      to: `/projects/${projectId}/calculators/wc`,
    },
    {
      key: "stairs",
      name: "Staircase Provision & Sizing",
      desc: "Size escape and convenience stairs with occupancy limits.",
      tokens: 1,
      status: "active",
      colorClass: "stairs",
      to: `/projects/${projectId}/calculators/stairs`,
    },
    {
      key: "riser",
      name: "Riser Calculator",
      desc: "Coordinate vertical risers for MEP and comms.",
      tokens: 1,
      status: "active",
      colorClass: "riser",
      to: `/projects/${projectId}/calculators/risers?project_id=${projectId}`,
    },
    {
      key: "uvalue",
      name: "U-value Calculator",
      desc: "Test façade build-ups and envelope performance.",
      tokens: 1,
      status: "active",
      colorClass: "uvalue",
      to: `/projects/${projectId}/calculators/u-values`,
    },
    {
      key: "massing",
      name: "3D Massing",
      desc: "Upload GLB models, configure floors, and generate persisted floor slices.",
      tokens: 1,
      status: "active",
      colorClass: "uvalue",
      to: `/projects/${projectId}/massing`,
    },
    {
      key: "cycles",
      name: "Cycle Spaces",
      desc: "Calculate cycle parking requirements and end-of-trip impact.",
      tokens: 1,
      status: "active",
      colorClass: "cycles",
      to: `/calculators/cycles?project_id=${projectId}`,
    },
    {
      key: "plant",
      name: "Plant Calculator",
      desc: "",
      tokens: null,
      status: "coming-soon",
      colorClass: "plant",
    },
    {
      key: "eot",
      name: "End of Trip Facilities",
      desc: "",
      tokens: null,
      status: "coming-soon",
      colorClass: "eot",
    },
  ];
}

function fmt(value) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n % 1 === 0 ? n.toString() : n.toFixed(2);
}

function makeOrdinal(n) {
  const map = [
    "First",
    "Second",
    "Third",
    "Fourth",
    "Fifth",
    "Sixth",
    "Seventh",
    "Eighth",
    "Ninth",
    "Tenth",
  ];
  return map[n - 1] || `${n}th`;
}

function computeLabels(rows) {
  if (!rows || rows.length === 0) return [];
  const idx = rows.findIndex(r => r.is_entrance_floor);
  const entranceIdx = idx >= 0 ? idx : 0;
  return rows.map((_, i) => {
    const d = i - entranceIdx;
    if (d === 0) return "Ground floor";
    if (d > 0) return `${makeOrdinal(d)} floor`;
    const n = Math.abs(d);
    return n === 1 ? "Basement" : `Basement ${n}`;
  });
}

function toTitleCase(value) {
  if (typeof value !== "string" || value.trim() === "") {
    return "";
  }

  return value
    .toLowerCase()
    .split(/([\s-/]+)/)
    .map(part => {
      if (/^[\s-/]+$/.test(part)) {
        return part;
      }

      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join("")
    .trim();
}
